"use client";

import { MobileGlobalSearch } from "./GlobalSearch";
import { MobileLanguageMenu } from "./SiteSelect";
import * as Dialog from "../../clients/Dialog";
import { cn } from "@/lib/utils";
import { useCallback, useMemo, useState } from "react";
import {
  ChevronRight,
  LogOut,
  Phone,
  UserRound,
  UserRoundPlus,
} from "lucide-react";
import { useScopedI18n } from "@/messages/client";
import { Button } from "@/components/ui/button";
import RouterLink from "@/components/ui/router-link";
import { usePathname, useSearchParams } from "next/navigation";
import useLocaleRouter from "@/hooks/useLocaleRouter";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { DirectusImage } from "@/components/DirectusImage";
import React from "react";
import { useUser } from "@/components/clients/LocaleToStorage";

type NavItemType = {
  title: string;
  href?: string;
  children?: NavItemType[];
} & {
  mode?: "link" | "card";
  icon?: string;
  description?: string;
  subtitle?: string;
  tel?: string;
  vertical?: boolean;
};

function UserDialog() {
  const t = useScopedI18n("全局导航条");

  const [open, setOpen] = useState(false);

  const { user, clearToken } = useUser();

  const locale = useCurrentLocale();

  const router = useLocaleRouter();

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const redirect_url = useMemo(() => {
    if (pathname === `${locale === "en" ? "" : "/" + locale}/login`) {
      return "";
    }

    if (pathname !== `/${locale}`) {
      const query = searchParams.toString();

      return encodeURI(`${pathname}${query ? `?${query}` : ""}`);
    }

    return `/${locale}`;
  }, [pathname, searchParams, locale]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <div
          className={cn("cursor-pointer text-[#2C2C2C]", {
            "text-primary": open,
          })}>
          <UserRound size={20} />
        </div>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="fixed left-0 top-11 z-40 h-24 w-full border-b border-[#E5E5E5] bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)] outline-none !duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-1/4 data-[state=open]:slide-in-from-top-1/4">
          <div className="flex h-full w-full items-center px-6">
            {!user ? (
              <div className="grid w-full grid-cols-2 items-center justify-between gap-5">
                <RouterLink href="/register">
                  <Button
                    variant="outline"
                    className="h-9 w-full space-x-1 rounded-full text-sm">
                    <UserRoundPlus size={16} />
                    <span>{t("注册")}</span>
                  </Button>
                </RouterLink>

                <Button
                  variant="outline"
                  className="h-9 space-x-1 rounded-full text-sm"
                  onClick={() => {
                    router.push(`/login?redirect_url=${redirect_url}`);
                  }}>
                  <LogOut size={16} />
                  <span>{t("登录")}</span>
                </Button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserRound />
                  <span>{user?.first_name}</span>
                </div>

                <Button
                  variant="outline"
                  className="h-9 space-x-1 rounded-full text-sm"
                  onClick={() => {
                    window.localStorage.removeItem("token");

                    clearToken?.();

                    router.refresh();
                  }}>
                  <LogOut size={16} />
                  <span>{t("退出登录")}</span>
                </Button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const MoreButton = React.forwardRef(function MoreButton(props: {
  open: boolean;
  setOpen?: (v: boolean) => void;
}) {
  return (
    <div
      className="relative h-3.5 w-4"
      onClick={() => {
        props.setOpen?.(!props.open);
      }}>
      <div
        className={cn(
          "absolute right-0 top-0 h-0.5 w-full rounded-full bg-[#2C2C2C] transition-transform duration-300",
          {
            "top-1/2 rotate-45": props.open,
          }
        )}
      />
      <div
        className={cn(
          "absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 rounded-full bg-[#2C2C2C] transition-opacity duration-300",
          {
            "opacity-0": props.open,
          }
        )}
      />
      <div
        className={cn(
          "absolute left-0 top-3 h-0.5 w-full rounded-full bg-[#2C2C2C] transition-transform duration-300",
          {
            "top-1/2 -rotate-45": props.open,
          }
        )}
      />
    </div>
  );
});

function NavItems(props: {
  navs: NavItemType;
  level?: number;
  onCloseNav?: () => void;
}) {
  const level = props.level ?? 0;

  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn("border-[#E5E5E5]", {
        "pl-4": level > 0,
        "border-b": level === 0,
      })}>
      <div
        className="flex h-11 items-center justify-between"
        onClick={() => {
          setOpen(!open);
        }}>
        <RouterLink
          href={props.navs?.href}
          onClick={() => {
            props.onCloseNav?.();
          }}
          className={cn("text-sm text-[#2C2C2C]", {
            "font-semibold": open,
          })}>
          {props.navs?.title}
        </RouterLink>

        {(props.navs?.children?.length ?? 0) > 0 && (
          <div
            className={cn("transition-transform duration-300", {
              "rotate-90": open,
            })}>
            <ChevronRight size={18} />
          </div>
        )}
      </div>

      {open &&
        props.navs?.children?.map((item, index) => (
          <NavItems
            key={index}
            navs={item}
            level={level + 1}
            onCloseNav={props.onCloseNav}
          />
        ))}
    </div>
  );
}

function LevelNav(props: { navs: NavItemType[]; onCloseNav?: () => void }) {
  return (
    <div className="flex flex-col bg-white p-4">
      {props.navs?.map((item, index) => (
        <NavItems
          key={index}
          navs={item}
          level={0}
          onCloseNav={props.onCloseNav}
        />
      ))}
    </div>
  );
}

function CardNav(props: { navs: NavItemType[]; onCloseNav?: () => void }) {
  const t = useScopedI18n("全局导航条");

  return (
    <div className="flex flex-col bg-white p-4">
      {props.navs?.map((item, index) => (
        <div
          className="flex min-h-[154px] w-full flex-col justify-center border-b border-[#E5E5E5] pb-4"
          key={index}>
          <div className="flex items-center justify-between py-2 text-base">
            <span>{item.title}</span>

            {item.icon && (
              <DirectusImage
                id={item.icon}
                width={20}
                height={20}
                alt={item.title}
              />
            )}
          </div>

          <div className="py-3 text-sm text-[#808080]">
            {item.description && <span>{item.description}</span>}

            {item.tel && (
              <div className="flex items-center space-x-2">
                <Phone size={16} />

                <span>{t("热线电话")}：{item.tel}</span>
              </div>
            )}
          </div>

          <RouterLink
            href={item.href}
            onClick={() => {
              props.onCloseNav?.();
            }}
            className="flex items-center space-x-4 text-sm text-[#383838]">
            <span>{item.subtitle}</span>

            <ChevronRight color="#CC2016" size={18} />
          </RouterLink>
        </div>
      ))}
    </div>
  );
}

function LinkNav(props: { navs: NavItemType[]; onCloseNav?: () => void }) {
  return (
    <div className="flex flex-col bg-white p-4">
      {props.navs?.map((item, index) => (
        <RouterLink
          href={item.href}
          className="flex h-11 items-center border-b border-[#E5E5E5] text-sm text-[#2C2C2C]"
          key={index}
          onClick={() => {
            props.onCloseNav?.();
          }}>
          {item.title}
        </RouterLink>
      ))}
    </div>
  );
}

function MenuDialog(props: { navs: NavItemType[] }) {
  const [open, setOpen] = useState(false);

  const [showIndex, setShowIndex] = useState(-1);

  const data = useMemo(() => {
    if (showIndex === -1) return null;
    return props.navs?.[showIndex]?.children ?? [];
  }, [props.navs, showIndex]);

  const onCloseNav = useCallback(() => {
    setOpen(false);
    setShowIndex(-1);
  }, [setOpen, setShowIndex]);

  const renderNav = useCallback(
    (data: NavItemType[]) => {
      if (data.length === 0 || showIndex === -1) return null;

      const item = data[0];

      if (item.mode && item.mode === "link" && item.children?.length) {
        return <LevelNav navs={data} onCloseNav={onCloseNav} />;
      }

      if (item.mode && item.mode === "card") {
        return <CardNav navs={data} onCloseNav={onCloseNav} />;
      }

      return <LinkNav navs={data} onCloseNav={onCloseNav} />;
    },
    [showIndex, onCloseNav]
  );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <MoreButton open={open} />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="fixed left-0 top-11 z-40 h-[calc(100vh-2.75rem)] w-full border-b border-[#E5E5E5] bg-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)] outline-none !duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-1/4 data-[state=open]:slide-in-from-top-1/4">
          <div className="flex h-full w-full overflow-hidden">
            <div className="w-28 flex-shrink-0 overflow-y-auto bg-[#F5F5F5]">
              {props.navs?.map((nav, index) => {
                return (
                  <RouterLink
                    key={index}
                    href={nav.children?.length ? undefined : nav.href}
                    onClick={() => {
                      setShowIndex(index);
                    }}
                    className={cn(
                      "relative flex min-h-[42px] cursor-pointer items-center py-2 pl-4 pr-1 text-sm text-[#2C2C2C] before:absolute before:left-0 before:top-1/2 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-primary before:opacity-0 before:transition-opacity before:duration-300",
                      {
                        "bg-white font-medium before:opacity-100":
                          showIndex === index,
                      }
                    )}>
                    {nav.title}
                  </RouterLink>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderNav(data ?? [])}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function MobileNav(props: { navs: NavItemType[] }) {
  return (
    <nav className="flex items-center gap-5 md:hidden">
      <MobileGlobalSearch />
      <UserDialog />
      <MobileLanguageMenu />
      <MenuDialog navs={props.navs} />
    </nav>
  );
}
